// What "+ Add condition" opens: the catalogue of checks, with what each one
// means, and anything already on this transition greyed out rather than hidden —
// so the list reads the same every time and an operator learns its shape.
//
// Split by DIRECTION, because the two halves are different questions. "Must be
// true" gates progress; "Must not be true" gates abandonment. A cancellation
// needs the second and almost never the first, so the relevant half is listed
// first and the other follows under its own heading.
//
// PORTALLED to the document body with fixed positioning. The layout that hosts
// this scrolls (`overflow-auto`), and an absolutely-positioned panel is clipped
// by its nearest scrolling ancestor — which chopped the top off the menu
// whenever the button sat high in the scroll area. Fixed positioning also lets
// the panel flip above or below the button depending on which side has room,
// and cap its own height to the space actually available.
import { cn } from '@my/ui';
import { Plus, Check } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  catalogueFor,
  type Polarity,
  type RequirementKind,
  type Subject,
} from '../lib/mockWorkflow';

const GROUP_TITLE: Record<Polarity, string> = {
  require: 'Must be true before the move',
  prevent: 'Prevents the move',
};

/** Panel width, and the gap kept from the button and the viewport edges. */
const PANEL_W = 432;
const GAP = 6;
const EDGE = 12;

export function AddConditionMenu({
  subject,
  preferred = 'require',
  alreadyAdded,
  onAdd,
}: {
  /**
   * Only checks that can apply to this subject are offered. An order has no
   * driver and a trip has no customer reference, so offering either would
   * invite a rule that can never be satisfied.
   */
  subject: Subject;
  /** Direction listed first — the one this transition is likely to want. */
  preferred?: Polarity;
  /** Ids (`kind:polarity`) already on the transition. */
  alreadyAdded: string[];
  onAdd: (kind: RequirementKind, polarity: Polarity) => void;
}) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<{ left: number; top?: number; bottom?: number; maxH: number }>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  /** Anchor the panel to the button, flipping to whichever side has more room. */
  const place = useCallback(() => {
    const r = buttonRef.current?.getBoundingClientRect();
    if (!r) return;
    const above = r.top - EDGE;
    const below = window.innerHeight - r.bottom - EDGE;
    const up = above > below;
    setBox({
      // Clamped so a button near the right edge doesn't push the panel off.
      left: Math.min(Math.max(r.left, EDGE), Math.max(EDGE, window.innerWidth - PANEL_W - EDGE)),
      ...(up
        ? { bottom: window.innerHeight - r.top + GAP }
        : { top: r.bottom + GAP }),
      maxH: Math.max(180, (up ? above : below) - GAP),
    });
  }, []);

  // Before paint, so the panel never renders in the wrong place first.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    // `true` for the capture phase: the scroll happens on an inner container,
    // which does not bubble to window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    // Click-away has to test BOTH nodes — the panel is portalled, so it is not
    // a descendant of the button's wrapper.
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!buttonRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  const order: Polarity[] =
    preferred === 'prevent' ? ['prevent', 'require'] : ['require', 'prevent'];
  const groups = order
    .map((polarity) => ({ polarity, items: catalogueFor(subject, polarity) }))
    .filter((g) => g.items.length > 0);

  const total = groups.reduce(
    (n, g) => n + g.items.filter((c) => !alreadyAdded.includes(`${c.kind}:${g.polarity}`)).length,
    0,
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"
      >
        <Plus className="h-3.5 w-3.5" />
        Add condition
      </button>

      {open &&
        box &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              left: box.left,
              top: box.top,
              bottom: box.bottom,
              width: PANEL_W,
              maxHeight: box.maxH,
            }}
            className="z-50 flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
          >
            <div className="flex-1 overflow-auto">
              {groups.map((group) => (
                <div key={group.polarity}>
                  <p
                    className={cn(
                      'sticky top-0 border-b border-slate-100 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide',
                      group.polarity === 'prevent'
                        ? 'bg-rose-50 text-rose-700'
                        : 'bg-sky-50 text-sky-700',
                    )}
                  >
                    {GROUP_TITLE[group.polarity]}
                  </p>
                  <ul>
                    {group.items.map((c) => {
                      const added = alreadyAdded.includes(`${c.kind}:${group.polarity}`);
                      return (
                        <li key={`${c.kind}:${group.polarity}`}>
                          <button
                            type="button"
                            disabled={added}
                            onClick={() => {
                              onAdd(c.kind, group.polarity);
                              // Stay open: adding two or three in a row is normal.
                              if (total <= 1) setOpen(false);
                            }}
                            className={cn(
                              'flex w-full items-start gap-2 px-3 py-2 text-left',
                              added ? 'cursor-default bg-slate-50' : 'hover:bg-sky-50',
                            )}
                          >
                            <span className="mt-0.5 w-3.5 shrink-0">
                              {added && <Check className="h-3.5 w-3.5 text-slate-400" />}
                            </span>
                            <span className="min-w-0">
                              <span
                                className={cn(
                                  'block text-sm',
                                  added ? 'text-slate-400' : 'text-slate-800',
                                )}
                              >
                                {c.label}
                                {c.crossTrack && (
                                  <span className="ml-1 rounded bg-violet-50 px-1 text-[10px] text-violet-700">
                                    other track
                                  </span>
                                )}
                                {added && <span className="ml-1 text-[11px]">· already added</span>}
                              </span>
                              <span className="block text-[11px] text-slate-400">
                                {group.polarity === 'prevent'
                                  ? (c.preventReason ?? c.detail)
                                  : c.detail}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <p className="shrink-0 border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] text-slate-400">
              Added as a warning. Promote it to Block when you are ready to enforce it.
            </p>
          </div>,
          document.body,
        )}
    </>
  );
}
